/**
 * Complete edge catalog for the Unwritten system
 *
 * Defines 50+ explicit edges representing relationships between domains.
 * Each edge can be used for:
 * - SurrealDB graph queries
 * - Frontend graph visualization
 * - Type-safe relationship traversal
 *
 * @module edges
 */
/**
 * Complete edge catalog
 * 50+ edges defining all major relationships between domains
 */
export const EDGE_CATALOG = [
    // ============================================================================
    // MEMORY EDGES (8 edges)
    // ============================================================================
    {
        id: 'memory_to_npc',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'metadata.npcIds',
        label: 'belongs to',
        cardinality: 'many-to-many',
    },
    {
        id: 'memory_to_player',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'player',
        toTable: 'player',
        relationship: 'references',
        fieldName: 'metadata.playerId',
        label: 'owned by',
        cardinality: 'many-to-one',
    },
    {
        id: 'memory_to_template',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'template',
        toTable: 'template',
        relationship: 'generates',
        fieldName: 'templateIds',
        label: 'generates',
        cardinality: 'many-to-many',
    },
    {
        id: 'memory_resonance_pattern',
        from: 'memory',
        fromTable: 'memory_resonance',
        to: 'memory',
        toTable: 'memory_pattern',
        relationship: 'references',
        fieldName: 'patternId',
        label: 'matches pattern',
        cardinality: 'many-to-one',
    },
    {
        id: 'memory_to_card',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'card',
        toTable: 'card',
        relationship: 'generates',
        fieldName: 'cardIds',
        label: 'generates',
        cardinality: 'many-to-many',
    },
    {
        id: 'memory_to_beat',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'beat',
        toTable: 'journey_beat',
        relationship: 'references',
        fieldName: 'beatIds',
        label: 'linked to',
        cardinality: 'many-to-many',
    },
    {
        id: 'memory_to_canon',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'modifies',
        label: 'affects canon',
        cardinality: 'many-to-many',
    },
    {
        id: 'memory_to_game',
        from: 'memory',
        fromTable: 'memory_facet',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'belongs to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // TEMPLATE EDGES (6 edges)
    // ============================================================================
    {
        id: 'template_to_card',
        from: 'template',
        fromTable: 'template',
        to: 'card',
        toTable: 'card',
        relationship: 'generates',
        label: 'creates card',
        cardinality: 'one-to-many',
        bidirectional: true,
    },
    {
        id: 'template_to_modifier',
        from: 'template',
        fromTable: 'template',
        to: 'modifier',
        toTable: 'modifier',
        relationship: 'references',
        fieldName: 'modifierIds',
        label: 'uses modifiers',
        cardinality: 'many-to-many',
    },
    {
        id: 'template_to_bias',
        from: 'template',
        fromTable: 'template',
        to: 'bias',
        toTable: 'bias',
        relationship: 'references',
        fieldName: 'biasIds',
        label: 'influenced by',
        cardinality: 'many-to-many',
    },
    {
        id: 'template_to_npc',
        from: 'template',
        fromTable: 'template',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'npcId',
        label: 'targets NPC',
        cardinality: 'many-to-one',
    },
    {
        id: 'template_to_goal',
        from: 'template',
        fromTable: 'template',
        to: 'goal',
        toTable: 'goal',
        relationship: 'references',
        fieldName: 'goalIds',
        label: 'supports goals',
        cardinality: 'many-to-many',
    },
    {
        id: 'template_to_game',
        from: 'template',
        fromTable: 'template',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'belongs to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // CARD EDGES (7 edges)
    // ============================================================================
    {
        id: 'card_to_template',
        from: 'card',
        fromTable: 'card',
        to: 'template',
        toTable: 'template',
        relationship: 'references',
        fieldName: 'templateId',
        label: 'instantiates',
        cardinality: 'many-to-one',
    },
    {
        id: 'card_to_npc',
        from: 'card',
        fromTable: 'card',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'targetNpcId',
        label: 'targets',
        cardinality: 'many-to-one',
    },
    {
        id: 'card_to_player',
        from: 'card',
        fromTable: 'card',
        to: 'player',
        toTable: 'player',
        relationship: 'references',
        fieldName: 'playerId',
        label: 'owned by',
        cardinality: 'many-to-one',
    },
    {
        id: 'card_to_modifier',
        from: 'card',
        fromTable: 'card',
        to: 'modifier',
        toTable: 'modifier',
        relationship: 'modifies',
        fieldName: 'appliedModifiers',
        label: 'applies',
        cardinality: 'many-to-many',
    },
    {
        id: 'card_to_beat',
        from: 'card',
        fromTable: 'card',
        to: 'beat',
        toTable: 'journey_beat',
        relationship: 'references',
        fieldName: 'beatId',
        label: 'plays in beat',
        cardinality: 'many-to-one',
    },
    {
        id: 'card_to_canon',
        from: 'card',
        fromTable: 'card',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'modifies',
        label: 'affects canon',
        cardinality: 'many-to-many',
    },
    {
        id: 'card_to_game',
        from: 'card',
        fromTable: 'card',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'belongs to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // BEAT EDGES (5 edges)
    // ============================================================================
    {
        id: 'beat_to_goal',
        from: 'beat',
        fromTable: 'journey_beat',
        to: 'goal',
        toTable: 'goal',
        relationship: 'references',
        fieldName: 'goalIds',
        label: 'progresses goals',
        cardinality: 'many-to-many',
    },
    {
        id: 'beat_to_npc',
        from: 'beat',
        fromTable: 'journey_beat',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'npcIds',
        label: 'involves NPCs',
        cardinality: 'many-to-many',
    },
    {
        id: 'beat_to_canon',
        from: 'beat',
        fromTable: 'journey_beat',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'modifies',
        label: 'impacts canon',
        cardinality: 'many-to-many',
    },
    {
        id: 'beat_to_season',
        from: 'beat',
        fromTable: 'journey_beat',
        to: 'season',
        toTable: 'season',
        relationship: 'stored_in',
        fieldName: 'seasonId',
        label: 'occurs in season',
        cardinality: 'many-to-one',
    },
    {
        id: 'beat_to_game',
        from: 'beat',
        fromTable: 'journey_beat',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'belongs to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // GOAL EDGES (4 edges)
    // ============================================================================
    {
        id: 'goal_to_npc',
        from: 'goal',
        fromTable: 'goal',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'npcId',
        label: 'belongs to NPC',
        cardinality: 'many-to-one',
    },
    {
        id: 'goal_parent_child',
        from: 'goal',
        fromTable: 'goal',
        to: 'goal',
        toTable: 'goal',
        relationship: 'references',
        fieldName: 'parentGoalId',
        label: 'subgoal of',
        cardinality: 'many-to-one',
    },
    {
        id: 'goal_to_canon',
        from: 'goal',
        fromTable: 'goal',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'references',
        fieldName: 'canonIds',
        label: 'driven by canon',
        cardinality: 'many-to-many',
    },
    {
        id: 'goal_to_game',
        from: 'goal',
        fromTable: 'goal',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'belongs to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // CANON EDGES (5 edges)
    // ============================================================================
    {
        id: 'canon_to_world',
        from: 'canon',
        fromTable: 'canon_entry',
        to: 'world',
        toTable: 'world',
        relationship: 'stored_in',
        fieldName: 'worldId',
        label: 'part of world',
        cardinality: 'many-to-one',
    },
    {
        id: 'canon_to_modifier',
        from: 'canon',
        fromTable: 'canon_entry',
        to: 'modifier',
        toTable: 'modifier',
        relationship: 'references',
        fieldName: 'modifierIds',
        label: 'has modifiers',
        cardinality: 'many-to-many',
    },
    {
        id: 'canon_to_npc',
        from: 'canon',
        fromTable: 'canon_entry',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'npcIds',
        label: 'involves NPCs',
        cardinality: 'many-to-many',
    },
    {
        id: 'canon_parent_child',
        from: 'canon',
        fromTable: 'canon_entry',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'references',
        fieldName: 'parentCanonId',
        label: 'derived from',
        cardinality: 'many-to-one',
    },
    {
        id: 'canon_to_game',
        from: 'canon',
        fromTable: 'canon_entry',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'applicable to game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // NPC EDGES (6 edges)
    // ============================================================================
    {
        id: 'npc_to_character',
        from: 'npc',
        fromTable: 'npc',
        to: 'character',
        toTable: 'character',
        relationship: 'references',
        fieldName: 'characterId',
        label: 'instances character',
        cardinality: 'many-to-one',
    },
    {
        id: 'npc_to_personality',
        from: 'npc',
        fromTable: 'npc',
        to: 'personality',
        toTable: 'personality',
        relationship: 'references',
        fieldName: 'personalityIds',
        label: 'has personality',
        cardinality: 'many-to-many',
    },
    {
        id: 'npc_to_bias',
        from: 'npc',
        fromTable: 'npc',
        to: 'bias',
        toTable: 'bias',
        relationship: 'references',
        fieldName: 'biasIds',
        label: 'has biases',
        cardinality: 'many-to-many',
    },
    {
        id: 'npc_to_capacity',
        from: 'npc',
        fromTable: 'npc',
        to: 'capacity',
        toTable: 'capacity',
        relationship: 'references',
        fieldName: 'capacityId',
        label: 'has capacity',
        cardinality: 'one-to-one',
    },
    {
        id: 'npc_to_world',
        from: 'npc',
        fromTable: 'npc',
        to: 'world',
        toTable: 'world',
        relationship: 'stored_in',
        fieldName: 'worldId',
        label: 'exists in world',
        cardinality: 'many-to-one',
    },
    {
        id: 'npc_to_game',
        from: 'npc',
        fromTable: 'npc',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'active in game',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // PLAYER EDGES (3 edges)
    // ============================================================================
    {
        id: 'player_to_game',
        from: 'player',
        fromTable: 'player',
        to: 'game',
        toTable: 'game',
        relationship: 'references',
        fieldName: 'gameId',
        label: 'playing game',
        cardinality: 'one-to-one',
    },
    {
        id: 'player_to_capacity',
        from: 'player',
        fromTable: 'player',
        to: 'capacity',
        toTable: 'capacity',
        relationship: 'references',
        fieldName: 'capacityId',
        label: 'has capacity',
        cardinality: 'one-to-one',
    },
    {
        id: 'player_to_burnout',
        from: 'player',
        fromTable: 'player',
        to: 'burnout',
        toTable: 'burnout',
        relationship: 'references',
        fieldName: 'burnoutId',
        label: 'has burnout state',
        cardinality: 'one-to-one',
    },
    // ============================================================================
    // CHARACTER EDGES (3 edges)
    // ============================================================================
    {
        id: 'character_to_world',
        from: 'character',
        fromTable: 'character',
        to: 'world',
        toTable: 'world',
        relationship: 'stored_in',
        fieldName: 'worldId',
        label: 'defined in world',
        cardinality: 'many-to-one',
    },
    {
        id: 'character_to_personality',
        from: 'character',
        fromTable: 'character',
        to: 'personality',
        toTable: 'personality',
        relationship: 'references',
        fieldName: 'basePersonalityIds',
        label: 'base personality',
        cardinality: 'many-to-many',
    },
    {
        id: 'character_to_canon',
        from: 'character',
        fromTable: 'character',
        to: 'canon',
        toTable: 'canon_entry',
        relationship: 'references',
        fieldName: 'canonIds',
        label: 'based on canon',
        cardinality: 'many-to-many',
    },
    // ============================================================================
    // SEASON EDGES (2 edges)
    // ============================================================================
    {
        id: 'season_to_game',
        from: 'season',
        fromTable: 'season',
        to: 'game',
        toTable: 'game',
        relationship: 'stored_in',
        fieldName: 'gameId',
        label: 'part of game',
        cardinality: 'many-to-one',
    },
    {
        id: 'season_sequence',
        from: 'season',
        fromTable: 'season',
        to: 'season',
        toTable: 'season',
        relationship: 'references',
        fieldName: 'previousSeasonId',
        label: 'follows',
        cardinality: 'many-to-one',
    },
    // ============================================================================
    // WORLD EDGES (2 edges)
    // ============================================================================
    {
        id: 'world_to_universe',
        from: 'world',
        fromTable: 'world',
        to: 'universe',
        toTable: 'universe_config',
        relationship: 'stored_in',
        fieldName: 'universeId',
        label: 'part of universe',
        cardinality: 'many-to-one',
    },
    {
        id: 'world_to_setting',
        from: 'world',
        fromTable: 'world',
        to: 'setting',
        toTable: 'setting',
        relationship: 'references',
        fieldName: 'settingIds',
        label: 'has settings',
        cardinality: 'one-to-many',
    },
    // ============================================================================
    // GAME EDGES (2 edges)
    // ============================================================================
    {
        id: 'game_to_world',
        from: 'game',
        fromTable: 'game',
        to: 'world',
        toTable: 'world',
        relationship: 'references',
        fieldName: 'worldId',
        label: 'set in world',
        cardinality: 'many-to-one',
    },
    {
        id: 'game_to_society',
        from: 'game',
        fromTable: 'game',
        to: 'society',
        toTable: 'society',
        relationship: 'references',
        fieldName: 'societyIds',
        label: 'includes societies',
        cardinality: 'one-to-many',
    },
    // ============================================================================
    // MODIFIER EDGES (2 edges)
    // ============================================================================
    {
        id: 'modifier_to_capacity',
        from: 'modifier',
        fromTable: 'modifier',
        to: 'capacity',
        toTable: 'capacity',
        relationship: 'modifies',
        label: 'affects capacity',
        cardinality: 'many-to-many',
    },
    {
        id: 'modifier_to_burnout',
        from: 'modifier',
        fromTable: 'modifier',
        to: 'burnout',
        toTable: 'burnout',
        relationship: 'modifies',
        label: 'triggers burnout',
        cardinality: 'many-to-many',
    },
    // ============================================================================
    // BIAS EDGES (1 edge)
    // ============================================================================
    {
        id: 'bias_to_modifier',
        from: 'bias',
        fromTable: 'bias',
        to: 'modifier',
        toTable: 'modifier',
        relationship: 'generates',
        label: 'generates modifiers',
        cardinality: 'one-to-many',
    },
    // ============================================================================
    // NARRATIVE SUB-DOMAIN EDGES (7 edges)
    // ============================================================================
    {
        id: 'irony_to_beat',
        from: 'narrative/irony',
        fromTable: 'narrative_irony',
        to: 'beat',
        toTable: 'journey_beat',
        relationship: 'modifies',
        label: 'adds irony to',
        cardinality: 'many-to-many',
    },
    {
        id: 'hooks_to_beat',
        from: 'narrative/hooks',
        fromTable: 'narrative_hook',
        to: 'beat',
        toTable: 'journey_beat',
        relationship: 'references',
        label: 'hooks into beat',
        cardinality: 'many-to-one',
    },
    {
        id: 'perception_to_memory',
        from: 'narrative/perception',
        fromTable: 'narrative_perception',
        to: 'memory',
        toTable: 'memory_facet',
        relationship: 'modifies',
        label: 'filters perception',
        cardinality: 'many-to-many',
    },
    {
        id: 'emotion_to_npc',
        from: 'narrative/emotion',
        fromTable: 'narrative_emotion',
        to: 'npc',
        toTable: 'npc',
        relationship: 'references',
        fieldName: 'npcId',
        label: 'emotional state of',
        cardinality: 'many-to-one',
    },
    {
        id: 'observation_to_memory',
        from: 'narrative/observation',
        fromTable: 'narrative_observation',
        to: 'memory',
        toTable: 'memory_facet',
        relationship: 'generates',
        label: 'creates memory',
        cardinality: 'one-to-many',
    },
    {
        id: 'resonance_to_memory',
        from: 'narrative/resonance',
        fromTable: 'narrative_resonance',
        to: 'memory',
        toTable: 'memory_resonance',
        relationship: 'modifies',
        label: 'calculates resonance',
        cardinality: 'one-to-many',
    },
    {
        id: 'novel_to_beat',
        from: 'narrative/novel',
        fromTable: 'narrative_chapter',
        to: 'beat',
        toTable: 'journey_beat',
        relationship: 'modifies',
        label: 'tracks novelty',
        cardinality: 'many-to-many',
    },
];
/**
 * Helper function to get all edges for a specific domain
 */
export function getEdgesForDomain(domainId) {
    return EDGE_CATALOG.filter((edge) => edge.from === domainId || edge.to === domainId);
}
/**
 * Helper function to get all edges between two domains
 */
export function getEdgesBetween(fromDomain, toDomain) {
    return EDGE_CATALOG.filter((edge) => edge.from === fromDomain && edge.to === toDomain);
}
/**
 * Helper function to get edge by ID
 */
export function getEdgeById(edgeId) {
    return EDGE_CATALOG.find((edge) => edge.id === edgeId);
}
/**
 * Helper function to get all bidirectional edges
 */
export function getBidirectionalEdges() {
    return EDGE_CATALOG.filter((edge) => edge.bidirectional === true);
}
/**
 * Helper function to get edges by relationship type
 */
export function getEdgesByType(type) {
    return EDGE_CATALOG.filter((edge) => edge.relationship === type);
}
//# sourceMappingURL=edges.js.map